import type { RequireAtLeastOne } from './params'
import type { Authors } from '~/composables/author/useAuthorQuery'
import type { NewestDocsParams, SearchLawAndLawCommentaryParams, SearchLiteratureParams } from '~/utils/solr/params'

export const SolrParamsKey = {
  ContentType: 'contentType[]',
  NotSecondLevelContentType: 'notSecondaryContentType[]',
  SearchFullText: 'searchFullText',
  GlobalSearch: 'globalSearch',
  LawId: 'lawId',
  PathLocation: 'pathLocation',
  CommentIdsByLawId: 'commentIdsByLawId',
  CommentByEzMainNodeId: 'commentByEzMainNodeId',
  LiteratureListByLawId: 'literatureListByLawId',
  InfoNoticeByEzObjectId: 'infoNoticeByEzObjectId',
  Templates: 'templates',
  Template: 'template',
  Authors: 'authors',
  Author: 'author',
  RemoteId: 'remoteId',
  Id: 'id', // book ID, chapter ID, or wildcard for related documents
  Ids: 'ids', // array of document IDs for batch fetching
  SearchLawAndLawCommentary: 'searchLawAndLawCommentary',
  BookHierarchy: 'bookHierarchy',
  Pages: 'pages', // array of two integers representing page counter range [min, max]
  Sort: 'sort', // sort parameter for ordering results
  PageNumberString: 'pageNumberString', // exact page number string filter
  SearchLiterature: 'searchLiterature',
  PublisherId: 'publisherId', // Custom param for journals endpoint to fetch a specific publisher and nested issues
  Newest: 'newest',
  LawReferenceId: 'lawReferenceId',
} as const

export const GLOBAL_SEARCH_REQUEST_KINDS = ['preview', 'full-results'] as const
export type GlobalSearchRequestKind = (typeof GLOBAL_SEARCH_REQUEST_KINDS)[number]

export type SolrParams = RequireAtLeastOne<{
  [SolrParamsKey.ContentType]: string[] // fetches by nodeType, f.ex. ['law', 'chapter']
  [SolrParamsKey.NotSecondLevelContentType]: string[] // excludes secondary nodeTypes, f.ex. ['law', 'chapter']
  [SolrParamsKey.SearchFullText]: string // full text search within context
  [SolrParamsKey.GlobalSearch]: string // global full text search
  [SolrParamsKey.LawId]: string // fetches all documents related to a law
  [SolrParamsKey.PathLocation]?: string // fetches all documents related to a path location (e.g., ezMainNodeId)
  [SolrParamsKey.CommentIdsByLawId]?: string // fetches law commentary IDs related to a law ID
  [SolrParamsKey.CommentByEzMainNodeId]?: string // fetches a law commentary document by a main node ID
  [SolrParamsKey.LiteratureListByLawId]?: string // fetches the literature list section for a law
  [SolrParamsKey.InfoNoticeByEzObjectId]?: string // fetches a currently valid info notice by its Ibexa object ID
  [SolrParamsKey.Authors]?: Authors // fetches all documents related to a path location, applies to Ibexa documents
  [SolrParamsKey.Author]?: string // fetches all documents related to a slug (remote ID)
  [SolrParamsKey.RemoteId]?: string // fetches document by Ibexa remoteId (stable identifier)
  [SolrParamsKey.Id]?: string // fetch document by exact ID or with wildcard for related documents
  [SolrParamsKey.Ids]?: string[] // fetch multiple documents by exact IDs
  [SolrParamsKey.SearchLawAndLawCommentary]?: SearchLawAndLawCommentaryParams // full text search for a comment and law defined by lawId and commentId
  [SolrParamsKey.BookHierarchy]?: string // fetches document by exact ID or with wildcard for related documents
  [SolrParamsKey.Pages]?: [number, number] // fetches pages with page counters in the range [min, max]
  [SolrParamsKey.Sort]?: string // sort parameter for ordering results
  [SolrParamsKey.PageNumberString]?: string // exact page number string filter
  [SolrParamsKey.SearchLiterature]?: SearchLiteratureParams // full text search for literature (books, articles, chapters, pages)
  [SolrParamsKey.PublisherId]?: string
  [SolrParamsKey.Newest]?: NewestDocsParams
  [SolrParamsKey.LawReferenceId]?: string // fetches all documents related to a law reference ID
}>
